import { createFeatureSelector, createSelector } from '@ngrx/store';
import { handleActions } from 'redux-actions';
import { AUTH_KEYS } from '../auth/auth-keys.constant';
import {
  ActionsEnum,
  SetRedirectPath,
  UpdateTokens,
} from './auth-data.actions';

const jwtDecode = require('jwt-decode');

// feature name
export const FEATURE_NAME = 'auth-data';

// state definition
export interface IState {
  accessToken: string;
  mfaExchangeToken: string;
  refreshToken: string;
  tokensLoaded: boolean;
  userId: string;
  accountId: string;
  authenticated: boolean;
  authenticatedThroughBilling: boolean;
  redirectPath: string;
}

// initial state
export const initialState: IState = {
  accessToken: void 0,
  mfaExchangeToken: void 0,
  refreshToken: void 0,
  tokensLoaded: void 0,
  userId: void 0,
  accountId: void 0,
  authenticated: void 0,
  authenticatedThroughBilling: void 0,
  redirectPath: '',
};

// reducer
const reducerFn = handleActions(
  {
    [ActionsEnum.AUTHENTICATED_THROUGH_BILLING]: function (
      state: IState
    ): IState {
      return {
        ...state,
        authenticatedThroughBilling: true,
        authenticated: false,
      };
    },
    [ActionsEnum.AUTHENTICATED]: function (state: IState): IState {
      return {
        ...state,
        authenticated: true,
      };
    },
    [ActionsEnum.UNAUTHENTICATED]: function (state: IState): IState {
      return {
        ...state,
        userId: null,
        accountId: null,
        authenticated: false,
      };
    },
    [ActionsEnum.SET_REDIRECT_PATH]: function (
      state: IState,
      { payload }: SetRedirectPath
    ): IState {
      return {
        ...state,
        redirectPath: payload.redirectPath,
      };
    },
    [ActionsEnum.UPDATE_TOKENS]: function (
      state: IState,
      { payload }: UpdateTokens
    ): IState {
      const { accessToken, refreshToken } = payload;

      if (accessToken) {
        const decodedJwt = jwtDecode(accessToken);

        return {
          ...state,
          accountId: decodedJwt[AUTH_KEYS.ACCOUNT_ID],
          userId: decodedJwt[AUTH_KEYS.USER_ID],
          mfaExchangeToken: decodedJwt[AUTH_KEYS.MFA_EXCHANGE_TOKEN],
          accessToken,
          refreshToken,
          tokensLoaded: true,
        };
      }

      return {
        ...state,
        accountId: null,
        userId: null,
        mfaExchangeToken: null,
        accessToken: null,
        refreshToken: null,
        tokensLoaded: false,
      };
    },
  } as any,
  initialState
);

// required for AOT
export function reducer(state: IState = initialState, action: any) {
  return reducerFn(state, action);
}

// selectors
const selectFeature = createFeatureSelector<IState>(FEATURE_NAME);

export const getAuthenticated = createSelector(
  selectFeature,
  (state: IState) => state.authenticated
);

export const getAuthenticatedThroughBilling = createSelector(
  selectFeature,
  (state: IState) => state.authenticatedThroughBilling
);

export const getRedirectPath = createSelector(
  selectFeature,
  (state: IState) => state.redirectPath
);

export const getAccountId = createSelector(
  selectFeature,
  (state: IState) => state.accountId
);

export const getUserId = createSelector(
  selectFeature,
  (state: IState) => state.userId
);

export const getAccessToken = createSelector(
  selectFeature,
  (state: IState) => state.accessToken
);

export const getMfaExchangeToken = createSelector(
  selectFeature,
  (state: IState) => state.mfaExchangeToken
);

export const getRefreshToken = createSelector(
  selectFeature,
  (state: IState) => state.refreshToken
);

export const getTokensLoaded = createSelector(
  selectFeature,
  (state: IState) => state.tokensLoaded
);
